import { _post } from "../../../libraries/fetch";
import { ensureProductionApiToken } from "../../../libraries/auth";
import { getSubscriptionReportURL } from "../../../libraries/apiUrl";

export function normalizeSubscriptionReportRows(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.response?.data)) return response.response.data;
  if (Array.isArray(response?.response)) return response.response;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.result?.data)) return response.result.data;
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response?.subscriptions)) return response.subscriptions;
  return [];
}

/**
 * Match subscription fee collection line total:
 * Total Sales = sum(pay/total)
 * Total Discount = sum(discount)
 * Tax = sum(tax)
 * Card total = Total Sales - Discount + Tax
 */
export function buildSubscriptionFeeSummary(rows) {
  let totalSales = 0;
  let totalDiscount = 0;
  let totalTax = 0;
  let totalCount = 0;

  if (!Array.isArray(rows)) {
    return { totalCount: 0, totalSales: 0, totalDiscount: 0, totalTax: 0, grandTotal: 0 };
  }

  for (const row of rows) {
    if (row?.isDelete) continue;
    totalCount += 1;
    totalSales += Number.parseInt(String(row?.total ?? row?.pay ?? 0), 10) || 0;
    totalDiscount += Number.parseInt(String(row?.discount ?? 0), 10) || 0;
    totalTax += Number.parseInt(String(row?.tax ?? row?.taxToAdd ?? 0), 10) || 0;
  }

  return {
    totalCount,
    totalSales,
    totalDiscount,
    totalTax,
    grandTotal: totalSales - totalDiscount + totalTax
  };
}

function isEmptyReportError(response) {
  const status = response?.status;
  const message = String(response?.message || response?.error || "");
  return (
    status === 404 ||
    /not found|no (subscription|fee)|no records|empty|record not found/i.test(message)
  );
}

function buildMonthPayload(month, year) {
  const monthNum = Number(month);
  const yearNum = Number(year);
  const startDate = new Date(yearNum, monthNum - 1, 1);
  const endDate = new Date(yearNum, monthNum, 0);
  const pad = (n) => String(n).padStart(2, "0");

  return {
    from:
      startDate.getFullYear() +
      "-" +
      pad(startDate.getMonth() + 1) +
      "-" +
      pad(startDate.getDate()),
    to:
      endDate.getFullYear() +
      "-" +
      pad(endDate.getMonth() + 1) +
      "-" +
      pad(endDate.getDate()),
    saleType: "All",
    conductedBy: "All"
  };
}

export async function fetchSubscriptionSales(branchId, month, year) {
  const resolvedBranchId =
    typeof branchId === "object" && branchId !== null
      ? String(branchId._id || branchId.id || "").trim()
      : String(branchId || "").trim();

  if (!resolvedBranchId) {
    return {
      rows: [],
      total: 0,
      from: "",
      to: "",
      summary: buildSubscriptionFeeSummary([]),
      error: "Waiting for branch..."
    };
  }

  await ensureProductionApiToken();

  // Selected month start + end dates
  const payload = buildMonthPayload(month, year);
  const { from, to } = payload;

  // Admin endpoint (same report service as branches/.../subscriptions/reports/summary)
  const response = await _post(getSubscriptionReportURL(resolvedBranchId), payload);

  const failed =
    response?.success === false ||
    response?.status === false ||
    response?.status === 401 ||
    response?.status === 403;

  if (failed) {
    if (isEmptyReportError(response)) {
      return {
        rows: [],
        total: 0,
        from,
        to,
        summary: buildSubscriptionFeeSummary([]),
        error: ""
      };
    }

    return {
      rows: [],
      total: 0,
      from,
      to,
      summary: buildSubscriptionFeeSummary([]),
      error: response?.message || response?.error || "Failed to load subscription sales"
    };
  }

  const rows = normalizeSubscriptionReportRows(response);
  const summary = buildSubscriptionFeeSummary(rows);

  return {
    rows,
    // KPI = sales - discount + tax
    total: summary.grandTotal,
    from,
    to,
    summary,
    error: ""
  };
}
